import { useState, useEffect } from 'react'
import { AlertCircle, CheckCircle, Mail, Users, Globe, Play, X } from 'lucide-react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import ConnectGmailButton from './ConnectGmailButton'

export default function SequenceActivationModal({ 
  sequence, 
  enrolledCount = 0,
  isOpen, 
  onClose, 
  onConfirm 
}) {
  const [emailStatus, setEmailStatus] = useState(null)
  const [loadingChecks, setLoadingChecks] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const [userConfirmed, setUserConfirmed] = useState(false)

  useEffect(() => {
    if (isOpen && sequence) {
      checkPrerequisites()
    }
  }, [isOpen, sequence])

  const checkPrerequisites = async () => {
    setLoadingChecks(true)
    try {
      const response = await api.get('/api/communication/email/status')
      setEmailStatus(response.data)
    } catch (error) {
      console.error('Failed to check email status:', error)
      setEmailStatus({ selectedProvider: 'gmail', providers: { gmail: { available: false } } })
    } finally {
      setLoadingChecks(false)
    }
  }

  const updateProvider = async (provider) => {
    try {
      const response = await api.patch('/api/communication/email/provider', { provider })
      setEmailStatus(response.data)
      toast.success("Mode d'envoi mis à jour")
    } catch (error) {
      toast.error(error?.response?.data?.error || "Impossible de changer le mode d'envoi")
    }
  }

  const handleConfirm = async () => {
    if (!canActivate || !userConfirmed) return
    
    setConfirming(true)
    try {
      await onConfirm()
      onClose()
    } catch (error) {
      // L'erreur est déjà gérée par la mutation parent
    } finally {
      setConfirming(false)
    }
  }

  if (!isOpen) return null

  // Calculs des checks
  const workflowNodes = sequence?.workflowData?.nodes || []
  // Compter les nodes de type email (vraies étapes d'action)
  const emailSteps = workflowNodes.filter(node => node.type === 'email')
  const stepsCount = emailSteps.length
  const hasValidSteps = stepsCount > 0
  const selectedProvider = emailStatus?.selectedProvider || 'gmail'
  const providerInfo = emailStatus?.providers?.[selectedProvider]
  const providerReady = providerInfo?.available === true
  const hasProspects = enrolledCount > 0

  const canActivate = providerReady && hasValidSteps
  const allChecksPass = canActivate && hasProspects

  const checks = [
    {
      id: 'email',
      label: selectedProvider === 'gmail' ? 'Gmail connecté' : 'SMTP configuré',
      status: loadingChecks ? 'loading' : (providerReady ? 'success' : 'error'),
      message: loadingChecks ? 'Vérification...' : (
        providerReady
          ? (selectedProvider === 'gmail'
              ? `Connecté : ${emailStatus?.providers?.gmail?.email || ''}`
              : 'SMTP prêt (MAILER_DSN)')
          : (selectedProvider === 'gmail' ? 'Gmail non connecté' : 'SMTP non configuré')
      ),
      critical: true
    },
    {
      id: 'steps',
      label: 'Étapes définies',
      status: hasValidSteps ? 'success' : 'error',
      message: hasValidSteps ? `${stepsCount} étape(s) configurée(s)` : 'Aucune étape définie',
      critical: true
    },
    {
      id: 'prospects',
      label: 'Prospects inscrits',
      status: hasProspects ? 'success' : 'warning',
      message: hasProspects ? `${enrolledCount} prospect(s) inscrit(s)` : 'Aucun prospect inscrit',
      critical: false
    }
  ]

  const criticalIssues = checks.filter(c => c.critical && c.status === 'error')
  const warnings = checks.filter(c => !c.critical && c.status === 'warning')

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgb(var(--rgb-shade) / 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'rgb(var(--rgb-surface-hover))',
        borderRadius: '16px',
        padding: '2rem',
        width: '100%',
        maxWidth: '500px',
        border: '1px solid rgb(var(--rgb-ink) / 0.1)'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '1.5rem' 
        }}>
          <div>
            <h2 style={{ 
              fontSize: '1.25rem', 
              fontWeight: 600, 
              marginBottom: '0.5rem', 
              color: 'var(--color-text)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Play size={20} style={{ color: '#5b5cf4' }} />
              Activer la séquence
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', margin: 0 }}>
              {sequence?.name}
            </p>
          </div>
          <button onClick={onClose} style={{ 
            background: 'transparent', 
            border: 'none', 
            color: 'var(--color-text-secondary)', 
            cursor: 'pointer' 
          }}>
            <X size={24} />
          </button>
        </div>

        {/* Checks */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ 
            fontSize: '1rem', 
            fontWeight: 600, 
            marginBottom: '1rem', 
            color: 'var(--color-text)' 
          }}>
            Vérifications préalables
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {checks.map(check => (
              <div key={check.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                background: 'rgb(var(--rgb-ink) / 0.02)',
                borderRadius: '8px',
                border: `1px solid ${
                  check.status === 'success' ? 'rgba(91, 92, 244,0.2)' :
                  check.status === 'error' ? 'rgba(239,68,68,0.2)' :
                  check.status === 'warning' ? 'rgba(245,158,11,0.2)' :
                  'rgb(var(--rgb-ink) / 0.1)'
                }`
              }}>
                {check.status === 'loading' ? (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(91, 92, 244,0.2)',
                    borderTop: '2px solid #5b5cf4',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : check.status === 'success' ? (
                  <CheckCircle size={20} style={{ color: '#5b5cf4' }} />
                ) : (
                  <AlertCircle size={20} style={{ 
                    color: check.status === 'error' ? '#ef4444' : '#f59e0b' 
                  }} />
                )}
                
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: 500, 
                    color: 'var(--color-text)', 
                    marginBottom: '0.25rem' 
                  }}>
                    {check.label}
                  </div>
                  <div style={{ 
                    fontSize: '0.85rem', 
                    color: 'var(--color-text-secondary)' 
                  }}>
                    {check.message}
                  </div>
                </div>

                {check.critical && (
                  <div style={{
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    background: check.status === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(91, 92, 244,0.1)',
                    color: check.status === 'error' ? '#ef4444' : '#5b5cf4'
                  }}>
                    {check.status === 'error' ? 'Requis' : 'OK'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        {canActivate && (
          <div style={{
            padding: '1rem',
            background: allChecksPass ? 'rgba(91, 92, 244,0.05)' : 'rgba(245,158,11,0.05)',
            border: `1px solid ${allChecksPass ? 'rgba(91, 92, 244,0.2)' : 'rgba(245,158,11,0.2)'}`,
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ 
              fontSize: '0.9rem', 
              fontWeight: 600, 
              marginBottom: '0.5rem', 
              color: 'var(--color-text)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Users size={16} />
              Résumé de l'activation
            </h4>
            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
              {hasProspects ? (
                <>
                  <strong style={{ color: '#5b5cf4' }}>{enrolledCount} prospect(s)</strong> vont entrer en séquence.<br />
                  Les emails commenceront à être envoyés dès la prochaine exécution du runner.
                </>
              ) : (
                <>
                  <strong style={{ color: '#f59e0b' }}>Aucun prospect inscrit</strong> - la séquence sera active mais n'enverra rien.<br />
                  Vous pourrez ajouter des prospects après activation.
                </>
              )}
            </div>
          </div>
        )}

        {/* Errors */}
        {criticalIssues.length > 0 && (
          <div style={{
            padding: '1rem',
            background: 'rgba(239,68,68,0.05)',
            border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <div style={{ 
              fontSize: '0.9rem', 
              fontWeight: 600, 
              marginBottom: '0.5rem', 
              color: '#ef4444' 
            }}>
              Impossible d'activer
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
              Veuillez corriger les problèmes critiques ci-dessus.
              {!providerReady && selectedProvider === 'gmail' && (
                <>
                  <br /><br />
                  <ConnectGmailButton
                    className=""
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#5b5cf4', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* Confirmation checkbox */}
        {canActivate && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem',
            padding: '0.75rem',
            background: 'rgb(var(--rgb-ink) / 0.02)',
            borderRadius: '8px'
          }}>
            <input
              type="checkbox"
              checked={userConfirmed}
              onChange={(e) => setUserConfirmed(e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                accentColor: '#5b5cf4'
              }}
            />
            <label style={{ fontSize: '0.9rem', color: 'var(--color-text)', cursor: 'pointer' }}>
              Je confirme vouloir activer cette séquence
            </label>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
            <span>Envoi via</span>
            <select
              value={selectedProvider}
              onChange={(e) => updateProvider(e.target.value)}
              style={{
                background: 'rgb(var(--rgb-ink) / 0.05)',
                border: '1px solid rgb(var(--rgb-ink) / 0.15)',
                borderRadius: '6px',
                color: 'var(--color-text)',
                padding: '0.35rem 0.5rem'
              }}
            >
              <option value="gmail">Gmail</option>
              <option value="smtp" disabled={emailStatus?.providers?.smtp?.available !== true}>SMTP</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.25rem',
                background: 'transparent',
                border: '1px solid rgb(var(--rgb-ink) / 0.2)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canActivate || !userConfirmed || confirming}
              style={{
                padding: '0.75rem 1.25rem',
                background: canActivate && userConfirmed && !confirming ? '#5b5cf4' : 'rgba(91, 92, 244,0.3)',
                border: 'none',
                borderRadius: '8px',
                color: '#0a0a0a',
                fontWeight: 600,
                cursor: canActivate && userConfirmed && !confirming ? 'pointer' : 'not-allowed',
                opacity: canActivate && userConfirmed && !confirming ? 1 : 0.7,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Play size={16} />
              {confirming ? 'Activation...' : 'Activer la séquence'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
